import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import LumetierLogo from "@/components/LumetierLogo";
import { exportPNG, exportSVG, exportWebM, exportGIF } from "@/lib/lumetierExport";

/* Preview + asset-download page for the Lumetier brand mark.
   Lets you replay the animation, switch light/dark, and download every common
   social-media asset (square PNGs in 3 sizes + transparent variants, vector SVG,
   plus an in-page video preview that becomes a WebM or GIF download). */
export default function LumetierPreview() {
  const [key, setKey] = useState(0);
  const [dark, setDark] = useState(false);
  const [busy, setBusy] = useState(null);
  // Video state lives separately so a generated preview can be replayed in-page and
  // downloaded later without re-rendering on every theme toggle.
  const [video, setVideo] = useState(null); // { url, blob, ext, dark }
  const [gifProgress, setGifProgress] = useState(null);
  const videoRef = useRef(null);

  const replay = () => setKey((k) => k + 1);

  // Drop the previously-generated preview when the theme changes — the existing video
  // is the wrong color now and would mislead the user about what they'd download.
  useEffect(() => {
    if (video && video.dark !== dark) {
      URL.revokeObjectURL(video.url);
      setVideo(null);
    }
  }, [dark, video]);

  async function handlePNG(size, bg) {
    setBusy(`png-${size}-${bg}`);
    try {
      await exportPNG({ size, bg, inverted: dark });
      toast.success(`Downloaded ${size}×${size} PNG`);
    } catch (err) {
      toast.error(err.message || "Couldn't export PNG");
    } finally { setBusy(null); }
  }

  function handleSVG() {
    setBusy("svg");
    try {
      exportSVG({ inverted: dark });
      toast.success("Downloaded SVG");
    } catch (err) {
      toast.error(err.message || "Couldn't export SVG");
    } finally { setBusy(null); }
  }

  async function handleGenerateVideo() {
    setBusy("webm");
    try {
      const result = await exportWebM({
        size: 1080,
        durationSec: 4.5,
        inverted: dark,
        download: false, // generate-then-preview, don't auto-save
        onProgress: (p) => setBusy(`webm-${Math.round(p * 100)}`),
      });
      setVideo({ ...result, dark });
      toast.success("Video ready — preview below, then tap Download.");
    } catch (err) {
      toast.error(err.message || "Couldn't generate video");
    } finally { setBusy(null); }
  }

  async function handleDownloadMP4() {
    if (!video) return;
    setBusy("mp4");
    try {
      // Send the in-memory WebM blob to the backend; ffmpeg there converts to H.264
      // MP4 with the +faststart flag — the only reliable format for iOS Photos /
      // Files / Safari / iMessage to play natively.
      const fd = new FormData();
      fd.append("file", video.blob, "lumetier.webm");
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${apiBase}/api/lumetier/convert-to-mp4`, {
        method: "POST",
        body: fd,
      });
      if (!resp.ok) throw new Error("Conversion failed on the server");
      const mp4 = await resp.blob();
      const url = URL.createObjectURL(mp4);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lumetier-logo${dark ? "-dark" : ""}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Downloaded .mp4 — plays everywhere on iOS");
    } catch (err) {
      toast.error(err.message || "Couldn't convert to MP4");
    } finally { setBusy(null); }
  }

  function handleDownloadWebM() {
    if (!video) return;
    const a = document.createElement("a");
    a.href = video.url;
    a.download = `lumetier-logo${dark ? "-dark" : ""}.${video.ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Downloaded .${video.ext} — drop into your social post`);
  }

  async function handleGIF() {
    setBusy("gif");
    setGifProgress(0);
    try {
      await exportGIF({
        size: 600,
        fps: 20,
        durationSec: 4.5,
        inverted: dark,
        onProgress: (p) => setGifProgress(Math.round(p * 100)),
      });
      toast.success("Downloaded animated GIF — plays everywhere");
    } catch (err) {
      toast.error(err.message || "Couldn't export GIF");
    } finally { setBusy(null); setGifProgress(null); }
  }

  const fg = dark ? "#fafaf9" : "#1c1917";
  const bg = dark ? "#0c0a09" : "#fafaf9";
  const sub = dark ? "rgba(250,250,249,0.55)" : "rgba(28,25,23,0.55)";

  return (
    <div
      className="min-h-screen flex flex-col items-center p-8 transition-colors"
      style={{ background: bg }}
      data-testid="lumetier-preview"
    >
      {/* Logo display */}
      <div className="flex-1 flex items-center justify-center w-full min-h-[520px]">
        <LumetierLogo key={key} size={300} inverted={dark} />
      </div>

      {/* Replay + theme */}
      <div className="flex items-center gap-3 mt-6">
        <button
          onClick={replay}
          data-testid="replay-btn"
          className="px-4 h-10 text-[11px] uppercase tracking-[0.22em] border hover:opacity-80 transition-opacity"
          style={{ color: fg, borderColor: fg }}
        >
          Replay animation
        </button>
        <button
          onClick={() => setDark((d) => !d)}
          data-testid="theme-btn"
          className="px-4 h-10 text-[11px] uppercase tracking-[0.22em] border hover:opacity-80 transition-opacity"
          style={{ color: fg, borderColor: fg }}
        >
          {dark ? "Light mode" : "Dark mode"}
        </button>
      </div>

      {/* Download panel */}
      <div
        className="w-full max-w-3xl mt-12 border-t pt-10 pb-12"
        style={{ borderColor: sub }}
      >
        <div className="text-center mb-8">
          <div className="text-[11px] uppercase tracking-[0.32em] mb-1" style={{ color: sub }}>Download assets</div>
          <h2 className="text-2xl font-light tracking-wide" style={{ color: fg, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Take Lumetier to social
          </h2>
          <p className="text-xs mt-2" style={{ color: sub }}>
            All assets export in the current theme ({dark ? "dark" : "light"}). Toggle theme above to switch.
          </p>
        </div>

        {/* === Animated section (the star of the show) === */}
        <div
          className="border p-5 mb-6"
          style={{ borderColor: fg, background: dark ? "rgba(250,250,249,0.04)" : "rgba(28,25,23,0.04)" }}
          data-testid="animated-section"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: sub }}>★ Animated for social</div>

          {/* Inline preview / generate */}
          {!video ? (
            <button
              onClick={handleGenerateVideo}
              disabled={busy && busy.startsWith("webm")}
              data-testid="generate-video-btn"
              className="w-full text-center py-8 border-2 border-dashed hover:opacity-80 disabled:opacity-50 transition-opacity"
              style={{ borderColor: sub, color: fg }}
            >
              <div className="text-lg mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                {busy && busy.startsWith("webm-") ? `Rendering ${busy.replace("webm-", "")}%…` : busy === "webm" ? "Starting…" : "Generate video preview"}
              </div>
              <div className="text-xs" style={{ color: sub }}>
                ↓ Click to render a 4.5-second 1080×1080 animation — then preview here and download.
              </div>
            </button>
          ) : (
            <div>
              <video
                ref={videoRef}
                src={video.url}
                autoPlay
                loop
                muted
                playsInline
                controls
                className="w-full max-w-md mx-auto block bg-black"
                style={{ aspectRatio: "1 / 1" }}
                data-testid="video-preview"
              />
              <div className="flex flex-wrap gap-3 justify-center mt-4">
                <button
                  onClick={handleDownloadMP4}
                  disabled={busy === "mp4"}
                  data-testid="download-mp4-btn"
                  className="px-5 h-11 text-[11px] uppercase tracking-[0.22em] border hover:opacity-80 disabled:opacity-50"
                  style={{ background: dark ? "#fafaf9" : "#1c1917", color: dark ? "#0c0a09" : "#fafaf9", borderColor: fg }}
                >
                  {busy === "mp4" ? "Converting…" : "★ Download .MP4 (iOS-friendly)"}
                </button>
                <button
                  onClick={handleDownloadWebM}
                  data-testid="download-webm-btn"
                  className="px-5 h-11 text-[11px] uppercase tracking-[0.22em] border hover:opacity-80"
                  style={{ color: fg, borderColor: fg }}
                >
                  Download .{video.ext}
                </button>
                <button
                  onClick={handleGIF}
                  disabled={busy === "gif"}
                  data-testid="download-gif-btn"
                  className="px-5 h-11 text-[11px] uppercase tracking-[0.22em] border hover:opacity-80 disabled:opacity-50"
                  style={{ color: fg, borderColor: fg }}
                >
                  {busy === "gif" ? (gifProgress != null ? `Encoding ${gifProgress}%…` : "Encoding…") : "Download .GIF"}
                </button>
                <button
                  onClick={() => { URL.revokeObjectURL(video.url); setVideo(null); }}
                  className="px-3 h-11 text-[11px] uppercase tracking-[0.22em] hover:opacity-80"
                  style={{ color: sub }}
                >
                  ✕ Re-render
                </button>
              </div>
              <div className="text-[11px] mt-3 text-center" style={{ color: sub }}>
                <strong style={{ color: fg }}>MP4</strong> ★ best for iOS — plays in Photos, iMessage, Files, Safari.{" "}
                <strong style={{ color: fg }}>WebM</strong> = highest quality for IG / TikTok / X upload UIs.{" "}
                <strong style={{ color: fg }}>GIF</strong> = universal fallback (Slack, email, older browsers).
              </div>
            </div>
          )}
        </div>

        {/* === Static PNG grid === */}
        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          <DownloadCard
            title="Profile picture" subtitle="400 × 400" caption="IG / X / LinkedIn avatar"
            onClick={() => handlePNG(400, dark ? "#0c0a09" : "#fafaf9")}
            loading={busy === `png-400-${dark ? "#0c0a09" : "#fafaf9"}`}
            fg={fg} sub={sub} testid="dl-png-400"
          />
          <DownloadCard
            title="Square post" subtitle="1080 × 1080" caption="Instagram feed post"
            onClick={() => handlePNG(1080, dark ? "#0c0a09" : "#fafaf9")}
            loading={busy === `png-1080-${dark ? "#0c0a09" : "#fafaf9"}`}
            fg={fg} sub={sub} testid="dl-png-1080"
          />
          <DownloadCard
            title="App icon" subtitle="2048 × 2048" caption="App stores, web favicon"
            onClick={() => handlePNG(2048, dark ? "#0c0a09" : "#fafaf9")}
            loading={busy === `png-2048-${dark ? "#0c0a09" : "#fafaf9"}`}
            fg={fg} sub={sub} testid="dl-png-2048"
          />
        </div>

        {/* === Transparent + vector === */}
        <div className="grid sm:grid-cols-2 gap-3">
          <DownloadCard
            title="Transparent PNG" subtitle="1080 × 1080" caption="Overlay on photos / colored backgrounds"
            onClick={() => handlePNG(1080, "transparent")}
            loading={busy === "png-1080-transparent"}
            fg={fg} sub={sub} testid="dl-png-transparent"
          />
          <DownloadCard
            title="Vector SVG" subtitle="∞ × ∞" caption="For print / Figma / Illustrator"
            onClick={handleSVG} loading={busy === "svg"}
            fg={fg} sub={sub} testid="dl-svg"
          />
        </div>
      </div>

      <p className="mt-4 text-[10px] uppercase tracking-[0.3em]" style={{ color: sub }}>
        /lumetier-preview · built by allure image
      </p>
    </div>
  );
}

function DownloadCard({ title, subtitle, caption, onClick, loading, fg, sub, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      data-testid={testid}
      className="text-left border p-4 hover:opacity-90 disabled:opacity-50 transition-opacity"
      style={{ borderColor: fg }}
    >
      <div className="text-sm" style={{ color: fg, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
        {title}
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] mt-1" style={{ color: fg }}>{subtitle}</div>
      <div className="text-[11px] mt-2" style={{ color: sub }}>{caption}</div>
      <div className="text-[11px] mt-3 uppercase tracking-[0.22em]" style={{ color: fg }}>
        {loading ? "Rendering…" : "Download →"}
      </div>
    </button>
  );
}
